import { Languages } from '../LanguageContext';
import { FormItem, FormMetadata, isPageBreak, QuestionType } from '../types';
import { isDateFieldType, isValidDateValue } from './dateNormalization';
import { getValidationMessage } from './validationMessages';
import {
  getVolunteerNameMismatchError,
  isVolunteerNameQuestionKey,
} from './volunteerNameValidation';

/**
 * Validates mandatory questions, min_value for select questions, date/datetime format,
 * and DONORNAME volunteer-name match against the name at the top of the form.
 * Returns a map of question_key -> error message for invalid questions.
 * Used by both the real submit flow (FormSubmitButton) and the demo preview submit.
 * Skips header and page_break (structural only).
 *
 * allQuestions is the full form (including off-page headers). Pass it when
 * validating a single page so DONORNAME can still be compared to the top name.
 */
export function validateMandatoryFormData(
  formData: FormMetadata,
  language: Languages,
  allQuestions: FormItem[] = formData.questions,
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  formData.questions.forEach((question) => {
    if (isPageBreak(question)) return;
    if (question.question_type === QuestionType.HEADER) return;
    if (
      question.rendered &&
      question.mandatory &&
      (!question.answer || question.answer.length === 0)
    ) {
      newErrors[question.question_key] = getValidationMessage(language, 'must_answer');
    }
    if (
      question.min_value !== undefined &&
      question.question_type === 'select' &&
      question.answer &&
      question.answer.length < question.min_value
    ) {
      newErrors[question.question_key] = getValidationMessage(language, 'select_min_options', {
        min: question.min_value,
      });
    }
    if (
      question.rendered &&
      isDateFieldType(question.field_type) &&
      question.answer &&
      question.answer.length > 0
    ) {
      const text = String(question.answer[0].text ?? '').trim();
      if (text !== '' && !isValidDateValue(text)) {
        newErrors[question.question_key] = getValidationMessage(language, 'date_invalid');
      }
    }
    if (isVolunteerNameQuestionKey(question.question_key)) {
      const typed = String(question.answer?.[0]?.text ?? '');
      const skipped = !question.rendered
        ? 'not_rendered'
        : newErrors[question.question_key]
          ? 'already_has_error'
          : null;
      if (!skipped) {
        const mismatch = getVolunteerNameMismatchError(allQuestions, typed, language);
        if (mismatch) {
          newErrors[question.question_key] = mismatch;
        }
        console.log('[DONORNAME]', {
          source: 'validateMandatoryFormData',
          question_key: question.question_key,
          typed,
          rendered: question.rendered,
          skipped: null,
          addedError: Boolean(mismatch),
          error: mismatch || '',
        });
      }
    }
  });

  return newErrors;
}
